import {
    useBlockProps,
    InspectorControls,
    BlockControls,
    BlockAlignmentToolbar,
} from "@wordpress/block-editor";
import {
    PanelBody,
    SelectControl,
    TextControl,
    Button,
    Disabled,
    Spinner,
    Notice,
    ToolbarGroup,
    ToolbarDropdownMenu,
} from "@wordpress/components";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import useLinkedProfile from "../../../shared/js/useLinkedProfile";
import { __, sprintf } from "@wordpress/i18n";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Inline", "content-egg"),  value: "inline" },
];

const DATA_SOURCE_OPTIONS = [
    { value: "auto",   label: __("Auto-fill from linked profile", "content-egg") },
    { value: "manual", label: __("Manual override (custom rating)", "content-egg") },
];

const BADGE_ICON_OPTIONS = [
    { label: __("Shield (guarantee)", "content-egg"), value: "shield-check" },
    { label: __("Badge (certification)", "content-egg"), value: "patch-check" },
    { label: __("Check", "content-egg"), value: "check-circle" },
];

const addItem   = (list, defaults, key, setAttributes) =>
    setAttributes({ [key]: [...list, defaults] });

const updateItem = (list, index, field, value, key, setAttributes) =>
    setAttributes({ [key]: list.map((item, i) => i === index ? { ...item, [field]: value } : item) });

const removeItem = (list, index, key, setAttributes) =>
    setAttributes({ [key]: list.filter((_, i) => i !== index) });

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({
        className: attributes.align ? `align${attributes.align}` : "",
    });

    const {
        variant,
        color_scheme,
        data_source,
        aggregate_rating,
        aggregate_rating_max,
        aggregate_count,
        aggregate_source,
        metrics,
        badges,
    } = attributes;

    const { loading: profileLoading, summary: linkedProfile } = useLinkedProfile();

    const useSource = !!linkedProfile && data_source !== "manual";

    const hasManualData =
        (aggregate_rating > 0 && aggregate_source) ||
        metrics.length > 0 ||
        badges.length > 0;
    const showPreview = useSource || hasManualData;

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
                <BlockAlignmentToolbar
                    value={attributes.align}
                    onChange={(val) => setAttributes({ align: val })}
                    controls={["wide", "full"]}
                />
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(val) => setAttributes({ variant: val })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={[
                            { label: __("Auto (site default)", "content-egg"), value: "auto" },
                            { label: __("Light", "content-egg"), value: "light" },
                            { label: __("Dark", "content-egg"), value: "dark" },
                        ]}
                        onChange={(val) => setAttributes({ color_scheme: val })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                {profileLoading && (
                    <PanelBody title={__("Data source", "content-egg")} initialOpen={false}>
                        <Spinner />
                    </PanelBody>
                )}

                {!profileLoading && linkedProfile && (
                    <PanelBody title={__("Data source", "content-egg")} initialOpen={true}>
                        <p style={{ fontSize: "12px", color: "#555", margin: "0 0 8px" }}>
                            {sprintf(
                                /* translators: %s: linked profile name */
                                __("Linked profile: %s", "content-egg"),
                                linkedProfile.profile_name
                            )}
                        </p>
                        <SelectControl
                            label={__("Aggregate rating source", "content-egg")}
                            help={__(
                                "Auto-fill pulls the aggregate rating from the linked profile at render time. Switch to manual to enter custom values for this block only. Metrics and badges are always entered here (or generated by AI) — never hydrated.",
                                "content-egg"
                            )}
                            value={data_source || "auto"}
                            options={DATA_SOURCE_OPTIONS}
                            onChange={(val) => setAttributes({ data_source: val })}
                            __nextHasNoMarginBottom
                        />
                    </PanelBody>
                )}

                <PanelBody title={__("Aggregate rating", "content-egg")} initialOpen={false}>
                    {useSource ? (
                        <Notice status="info" isDismissible={false}>
                            {sprintf(
                                /* translators: %s: linked profile name */
                                __("Aggregate rating will be hydrated from %s on save.", "content-egg"),
                                linkedProfile.profile_name
                            )}
                        </Notice>
                    ) : (
                        <>
                            <TextControl
                                label={__("Score", "content-egg")}
                                help={__("e.g. 4.8 — leave 0 to hide.", "content-egg")}
                                type="number"
                                min={0}
                                max={10}
                                step={0.1}
                                value={aggregate_rating || ""}
                                onChange={(val) => setAttributes({ aggregate_rating: parseFloat(val) || 0 })}
                                __nextHasNoMarginBottom
                            />
                            <TextControl
                                label={__("Max rating", "content-egg")}
                                type="number"
                                min={1}
                                max={10}
                                value={aggregate_rating_max || 5}
                                onChange={(val) => setAttributes({ aggregate_rating_max: parseFloat(val) || 5 })}
                                __nextHasNoMarginBottom
                            />
                            <TextControl
                                label={__("Review count", "content-egg")}
                                help={__("Leave 0 to omit.", "content-egg")}
                                type="number"
                                min={0}
                                value={aggregate_count || ""}
                                onChange={(val) => setAttributes({ aggregate_count: parseInt(val) || 0 })}
                                __nextHasNoMarginBottom
                            />
                            <TextControl
                                label={__("Source", "content-egg")}
                                help={__("e.g. G2, Trustpilot", "content-egg")}
                                value={aggregate_source}
                                onChange={(val) => setAttributes({ aggregate_source: val })}
                                __nextHasNoMarginBottom
                            />
                        </>
                    )}
                </PanelBody>

                <PanelBody title={__("Metrics", "content-egg")} initialOpen={true}>
                    {metrics.map((item, i) => (
                        <div key={i} style={{ borderBottom: "1px solid #ddd", paddingBottom: "10px", marginBottom: "10px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                                <strong style={{ fontSize: "12px" }}>{item.value || __("Metric", "content-egg") + " " + (i + 1)}</strong>
                                <Button isDestructive size="small" icon="no-alt" onClick={() => removeItem(metrics, i, "metrics", setAttributes)} label={__("Remove", "content-egg")} />
                            </div>
                            <TextControl
                                label={__("Value", "content-egg")}
                                placeholder="9,400+"
                                value={item.value}
                                onChange={(val) => updateItem(metrics, i, "value", val, "metrics", setAttributes)}
                                __nextHasNoMarginBottom
                            />
                            <TextControl
                                label={__("Label", "content-egg")}
                                placeholder={__("customers", "content-egg")}
                                value={item.label}
                                onChange={(val) => updateItem(metrics, i, "label", val, "metrics", setAttributes)}
                                __nextHasNoMarginBottom
                            />
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={() => addItem(metrics, { value: "", label: "" }, "metrics", setAttributes)}>
                        {__("Add metric", "content-egg")}
                    </Button>
                </PanelBody>

                <PanelBody title={__("Badges", "content-egg")} initialOpen={false}>
                    {badges.map((item, i) => (
                        <div key={i} style={{ borderBottom: "1px solid #ddd", paddingBottom: "10px", marginBottom: "10px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                                <strong style={{ fontSize: "12px" }}>{item.label || __("Badge", "content-egg") + " " + (i + 1)}</strong>
                                <Button isDestructive size="small" icon="no-alt" onClick={() => removeItem(badges, i, "badges", setAttributes)} label={__("Remove", "content-egg")} />
                            </div>
                            <TextControl
                                label={__("Label", "content-egg")}
                                placeholder={__("30-day money-back guarantee", "content-egg")}
                                value={item.label}
                                onChange={(val) => updateItem(badges, i, "label", val, "badges", setAttributes)}
                                __nextHasNoMarginBottom
                            />
                            <div style={{ marginTop: "8px" }}>
                                <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                                    {__("Icon", "content-egg")}
                                </div>
                                <SelectControl
                                    value={item.icon || "shield-check"}
                                    options={BADGE_ICON_OPTIONS}
                                    onChange={(val) => updateItem(badges, i, "icon", val, "badges", setAttributes)}
                                    __nextHasNoMarginBottom
                                />
                            </div>
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={() => addItem(badges, { label: "", icon: "shield-check" }, "badges", setAttributes)}>
                        {__("Add badge", "content-egg")}
                    </Button>
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {showPreview ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/trust-signals"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Add trust signals in the sidebar.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
